import { Post } from '../../schema-copy/feed-schema';

/**
 * Posts and images stored for offline reading, in IndexedDB.
 *
 * - posts: one record per post, keyed by [userId, feedId, postIndex], so a feed's posts form a
 *   contiguous key range that can be listed, read and deleted without touching other feeds or users.
 * - images: the images of a post as binary data ({ userId, url, type, data }), keyed by
 *   [userId, feedId, postIndex, url]. They sit next to their post's key, so deleting a post deletes
 *   its images. Index 'byUrl' ([userId, url]) finds an image by URL for display.
 * - icons: one feed icon per feed ({ url, type, data }), keyed by [userId, feedId].
 * - imageQueue: [userId, feedId, postIndex] of stored posts whose images haven't been handled yet.
 *
 * Every function is best-effort: failures are logged and return an empty result, so a broken or
 * missing IndexedDB only means posts and images come from the network as usual.
 */

const DB_NAME = 'downread-offline-posts';
const POSTS = 'posts';
const IMAGES = 'images';
const ICONS = 'icons';
const IMAGE_QUEUE = 'imageQueue';

type PostKey = [string, string, number];

/** A stored image or icon. */
export interface StoredImage {
  url: string;
  /** Content type, e.g. 'image/webp'. */
  type: string;
  data: ArrayBuffer;
}

interface ImageRecord extends StoredImage {
  userId: string;
}

let dbPromise: Promise<IDBDatabase> | undefined;

function openDb(): Promise<IDBDatabase> {
  if (typeof indexedDB == 'undefined') return Promise.reject(new Error('IndexedDB unavailable'));
  return dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 3);
    request.onupgradeneeded = event => {
      const db = request.result;
      const tx = request.transaction!;
      if (!db.objectStoreNames.contains(POSTS)) db.createObjectStore(POSTS);
      if (!db.objectStoreNames.contains(IMAGES)) db.createObjectStore(IMAGES).createIndex('byUrl', ['userId', 'url']);
      if (!db.objectStoreNames.contains(ICONS)) db.createObjectStore(ICONS);
      if (!db.objectStoreNames.contains(IMAGE_QUEUE)) db.createObjectStore(IMAGE_QUEUE);
      // Versions 1 and 2 stored posts without queueing them for their images (v2 only queued new
      // ones after the first images release): queue them all, so their images get downloaded too.
      if (event.oldVersion >= 1 && event.oldVersion < 3) {
        const queue = tx.objectStore(IMAGE_QUEUE);
        const cursor = tx.objectStore(POSTS).openKeyCursor();
        cursor.onsuccess = () => {
          if (!cursor.result) return;
          queue.put(1, cursor.result.key);
          cursor.result.continue();
        };
      }
    };
    // Another tab with an older version open would block the upgrade; it closes its connection
    // (onversionchange below) and loads the new data when it reopens.
    request.onblocked = () => console.warn('post-store: upgrade waiting for other tabs to close the database');
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        dbPromise = undefined;
      };
      resolve(db);
    };
    request.onerror = () => reject(request.error);
  }).catch(e => {
    dbPromise = undefined;
    throw e;
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function write(stores: string[], fn: (tx: IDBTransaction) => void): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(stores, 'readwrite');
    fn(tx);
    tx.oncomplete = () => resolve();
    tx.onerror = tx.onabort = () => reject(tx.error);
  });
}

async function read<T>(store: string, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return requestResult(fn(db.transaction(store, 'readonly').objectStore(store)));
}

// Arrays sort after numbers and strings, so `[]` as the last element is an upper bound for "any
// key that continues with these elements".
const prefixRange = (...prefix: (string | number)[]) => IDBKeyRange.bound(prefix, [...prefix, []]);

/** Store posts of one feed (overwriting what's there) and queue them for their images. */
export async function putPosts(userId: string, feedId: string, posts: Post[]): Promise<void> {
  if (!posts.length) return;
  try {
    await write([POSTS, IMAGE_QUEUE], tx => posts.forEach(post => {
      tx.objectStore(POSTS).put(post, [userId, feedId, post.number]);
      tx.objectStore(IMAGE_QUEUE).put(1, [userId, feedId, post.number]);
    }));
  } catch (e) {
    console.error('post-store: failed to store posts', feedId, e);
    throw e;
  }
}

/** The stored posts among `indices`, by index. */
export async function getStoredPosts(userId: string, feedId: string, indices: number[]): Promise<Map<number, Post>> {
  const result = new Map<number, Post>();
  if (!indices.length) return result;
  try {
    const db = await openDb();
    const store = db.transaction(POSTS, 'readonly').objectStore(POSTS);
    const posts = await Promise.all(indices.map(i => requestResult<Post | undefined>(store.get([userId, feedId, i]))));
    posts.forEach((post, n) => { if (post) result.set(indices[n], post); });
  } catch (e) {
    console.error('post-store: failed to read posts', feedId, e);
  }
  return result;
}

export async function getStoredPost(userId: string, feedId: string, index: number): Promise<Post | undefined> {
  return (await getStoredPosts(userId, feedId, [index])).get(index);
}

/** Indices of all stored posts of a feed. */
export async function getStoredIndices(userId: string, feedId: string): Promise<Set<number>> {
  try {
    const keys = await read(POSTS, store => store.getAllKeys(prefixRange(userId, feedId)));
    return new Set(keys.map(k => (k as PostKey)[2]));
  } catch (e) {
    console.error('post-store: failed to list posts', feedId, e);
    return new Set();
  }
}

/** Ids of all feeds that have stored posts or an icon for this user. */
export async function getStoredFeedIds(userId: string): Promise<string[]> {
  try {
    const db = await openDb();
    const tx = db.transaction([POSTS, ICONS], 'readonly');
    const listFeeds = (store: IDBObjectStore) => new Promise<string[]>((resolve, reject) => {
      const feedIds: string[] = [];
      const request = store.openKeyCursor(prefixRange(userId));
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return resolve(feedIds);
        const feedId = (cursor.key as PostKey)[1];
        feedIds.push(feedId);
        cursor.continue([userId, feedId, []]); // jump to the next feed
      };
      request.onerror = () => reject(request.error);
    });
    const [withPosts, withIcons] = await Promise.all([listFeeds(tx.objectStore(POSTS)), listFeeds(tx.objectStore(ICONS))]);
    return Array.from(new Set([...withPosts, ...withIcons]));
  } catch (e) {
    console.error('post-store: failed to list feeds', e);
    return [];
  }
}

/** Delete single posts of a feed, with their images. */
export async function deletePosts(userId: string, feedId: string, indices: number[]): Promise<void> {
  if (!indices.length) return;
  try {
    await write([POSTS, IMAGES, IMAGE_QUEUE], tx => indices.forEach(i => {
      tx.objectStore(POSTS).delete([userId, feedId, i]);
      tx.objectStore(IMAGES).delete(prefixRange(userId, feedId, i));
      tx.objectStore(IMAGE_QUEUE).delete([userId, feedId, i]);
    }));
  } catch (e) {
    console.error('post-store: failed to delete posts', feedId, e);
  }
}

/** Delete everything stored for one feed: posts, images and icon. */
export async function deleteFeed(userId: string, feedId: string): Promise<void> {
  try {
    await write([POSTS, IMAGES, ICONS, IMAGE_QUEUE], tx => {
      tx.objectStore(POSTS).delete(prefixRange(userId, feedId));
      tx.objectStore(IMAGES).delete(prefixRange(userId, feedId));
      tx.objectStore(IMAGE_QUEUE).delete(prefixRange(userId, feedId));
      tx.objectStore(ICONS).delete([userId, feedId]);
    });
  } catch (e) {
    console.error('post-store: failed to delete feed', feedId, e);
  }
}

// --- images ---

/** Posts whose images still have to be handled, as [feedId, postIndex]. */
export async function getImageQueue(userId: string): Promise<[string, number][]> {
  try {
    const keys = await read(IMAGE_QUEUE, store => store.getAllKeys(prefixRange(userId)));
    return keys.map(k => [(k as PostKey)[1], (k as PostKey)[2]]);
  } catch (e) {
    console.error('post-store: failed to read the image queue', e);
    return [];
  }
}

export async function dequeueImages(userId: string, feedId: string, postIndex: number): Promise<void> {
  try {
    await write([IMAGE_QUEUE], tx => tx.objectStore(IMAGE_QUEUE).delete([userId, feedId, postIndex]));
  } catch (e) {
    console.error('post-store: failed to update the image queue', e);
  }
}

export async function putImage(userId: string, feedId: string, postIndex: number, image: StoredImage): Promise<void> {
  const record: ImageRecord = { userId, ...image };
  await write([IMAGES], tx => tx.objectStore(IMAGES).put(record, [userId, feedId, postIndex, image.url]));
}

/** URLs of the images stored for a post. */
export async function getStoredImageUrls(userId: string, feedId: string, postIndex: number): Promise<Set<string>> {
  try {
    const keys = await read(IMAGES, store => store.getAllKeys(prefixRange(userId, feedId, postIndex)));
    return new Set(keys.map(k => (k as [string, string, number, string])[3]));
  } catch (e) {
    console.error('post-store: failed to list images', feedId, e);
    return new Set();
  }
}

/** All images stored for a post. */
export async function getPostImages(userId: string, feedId: string, postIndex: number): Promise<StoredImage[]> {
  try {
    return await read<ImageRecord[]>(IMAGES, store => store.getAll(prefixRange(userId, feedId, postIndex)));
  } catch (e) {
    console.error('post-store: failed to read images', feedId, e);
    return [];
  }
}

/** Any stored image with this URL. */
export async function getImageByUrl(userId: string, url: string): Promise<StoredImage | undefined> {
  try {
    return await read<ImageRecord | undefined>(IMAGES, store => store.index('byUrl').get([userId, url]));
  } catch (e) {
    console.error('post-store: failed to read image', url, e);
    return undefined;
  }
}

export async function putIcon(userId: string, feedId: string, icon: StoredImage): Promise<void> {
  await write([ICONS], tx => tx.objectStore(ICONS).put(icon, [userId, feedId]));
}

/** The stored icon URL of each feed that has one. */
export async function getStoredIconUrls(userId: string): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const request = db.transaction(ICONS, 'readonly').objectStore(ICONS).openCursor(prefixRange(userId));
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return resolve();
        result.set((cursor.key as [string, string])[1], (cursor.value as StoredImage).url);
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (e) {
    console.error('post-store: failed to list icons', e);
  }
  return result;
}

/** All stored feed icons of the user. */
export async function getIcons(userId: string): Promise<StoredImage[]> {
  try {
    return await read<StoredImage[]>(ICONS, store => store.getAll(prefixRange(userId)));
  } catch (e) {
    console.error('post-store: failed to read icons', e);
    return [];
  }
}

// --- clearing ---

/**
 * Delete all stored posts, images and icons ("Clear cache", logout) by deleting the whole
 * database. Deleting records alone doesn't give the disk space back: browsers compact their
 * IndexedDB files only later, so the storage used would hardly drop. The database only ever holds
 * the logged-in user's data (logout deletes it), so nobody else's is lost. The next access
 * recreates it empty.
 */
export async function clearAllPosts(): Promise<void> {
  if (typeof indexedDB == 'undefined') return;
  const open = dbPromise;
  dbPromise = undefined;
  try { (await open)?.close(); } catch { /* never opened */ }
  await new Promise<void>(resolve => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => {
      console.error('post-store: failed to delete the database', request.error);
      resolve();
    };
    // Other tabs close their connection on versionchange (see openDb); then this completes.
    request.onblocked = () => console.warn('post-store: deletion waiting for other tabs to close the database');
  });
}
